import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useGetActiveTeams } from "../../../../components/TimeTracker/TimeTrackerOverview/useTimeTracker.jsx";
import userType from "../../../../constants/userType.js";
import crssrImage from "../../../../images/singleinvoice/cross-red.svg";
import Delete from "../../../../images/icons/delete.svg";
import "./MvpProjectAlertsModal.css";

const ROLE_OPTIONS = [
  { key: "Super_Admin", label: "All Super Admins" },
  { key: "Admin", label: "All Admins" },
  { key: "Project_Manager", label: "All Project Managers" },
  { key: "Team_Manager", label: "All Team Managers" },
  { key: "User", label: "All Users" },
];

function roleIds(teams, key) {
  return (teams || [])
    .filter((team) => team.role_id === userType[key])
    .map((team) => team._id);
}

function roleForIds(teams, ids) {
  const selected = ids || [];
  if (!selected.length) return "Admin";
  const match = ROLE_OPTIONS.find((role) => {
    const idsForRole = roleIds(teams, role.key);
    return idsForRole.length > 0
      && idsForRole.length === selected.length
      && idsForRole.every((id) => selected.includes(id));
  });
  return match ? match.key : "Admin";
}

const MvpProjectAlertsModal = ({ isOpen, onClose, recordId, clientId, thresholdFor = "projects" }) => {
  const token = useSelector((state) => state.auth.token);
  const { data: teams = [] } = useGetActiveTeams(token);
  const [timeOn, setTimeOn] = useState(false);
  const [budgetOn, setBudgetOn] = useState(false);
  const [timePercent, setTimePercent] = useState(70);
  const [budgetPercent, setBudgetPercent] = useState(70);
  const [timeRole, setTimeRole] = useState("Admin");
  const [budgetRole, setBudgetRole] = useState("Admin");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !recordId || !token) return;
    let cancelled = false;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const load = async (thresholdType) => {
      try {
        const response = await axios.post(
          "/api/get/project/thresholds",
          { projectId: recordId, thresholdType },
          config
        );
        return response.status === 200 ? response.data?.data : null;
      } catch (_error) {
        return null;
      }
    };
    Promise.all([load("time"), load("budget")]).then(([timeData, budgetData]) => {
      if (cancelled) return;
      setTimeOn(!!timeData);
      setBudgetOn(!!budgetData);
      setTimePercent(timeData?.thresholdPercentage || 70);
      setBudgetPercent(budgetData?.thresholdPercentage || 70);
      setTimeRole(roleForIds(teams, timeData?.alertTo));
      setBudgetRole(roleForIds(teams, budgetData?.alertTo));
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, recordId, token, teams]);

  const saveOne = (type, enabled, percent, role) => axios.post(
    "/api/project/sprint/threshold",
    {
      thresholdFor,
      recordId,
      clientId,
      thresholdType: type,
      thresholdPercentage: enabled ? Number(percent) || 0 : 0,
      alertTo: enabled ? roleIds(teams, role) : [],
      thresholdTimeStatus: type === "time" ? enabled : false,
      thresholdBudgetStatus: type === "budget" ? enabled : false,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const save = async (clear) => {
    if (!recordId || !clientId) return;
    setSaving(true);
    try {
      await Promise.all([
        saveOne("time", clear ? false : timeOn, timePercent, timeRole),
        saveOne("budget", clear ? false : budgetOn, budgetPercent, budgetRole),
      ]);
      toast.success(clear ? "Alert deleted." : "Alert saved.", {
        position: "bottom-right",
        autoClose: 1500,
      });
      onClose();
    } catch (_error) {
      toast.error("Unable to save the alert.", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="mvp-alert-modal"
      overlayClassName="mvp-alert-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Project Alerts"
      style={{
        overlay: {
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(40, 40, 40, 0.28)",
          zIndex: 10000,
        },
        content: {
          position: "relative",
          inset: "auto",
          top: "auto",
          left: "auto",
          right: "auto",
          bottom: "auto",
          transform: "none",
          width: "min(500px, calc(100vw - 32px))",
          maxWidth: "500px",
          height: "auto",
          maxHeight: "calc(100vh - 48px)",
          margin: "0",
          overflow: "auto",
        },
      }}
    >
      <div className="modal-close-invite outter-modal-main mvp-alert-close">
        <button type="button" onClick={onClose} aria-label="Close">
          <img src={crssrImage} alt="" />
        </button>
      </div>
      <div className="mvp-alert-head">
        <div className="mvp-alert-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9.4 0.76V2.28" stroke="#A74E4E" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M9.4 22.75C8.11 22.75 7.07 21.71 7.07 20.42V19.05H11.73V20.42C11.73 21.71 10.69 22.75 9.4 22.75Z" stroke="#A74E4E" strokeWidth="1.5" />
            <path d="M15.72 12.17V15.23H3.08V8.96C3.08 5.47 5.91 2.64 9.4 2.64C12.89 2.64 15.72 5.47 15.72 8.96V12.17Z" stroke="#A74E4E" strokeWidth="1.5" />
            <path d="M0.75 17.14C0.75 18.2 1.6 19.05 2.66 19.05H16.14C17.2 19.05 18.05 18.2 18.05 17.14C18.05 16.09 17.2 15.23 16.14 15.23H2.66C1.6 15.23 0.75 16.09 0.75 17.14Z" stroke="#A74E4E" strokeWidth="1.5" />
          </svg>
          {thresholdFor === "sprints" ? "Sprint Alerts" : "Project Alerts"}
        </div>
      </div>

      <div className="mvp-alert-block">
        <label className="mvp-alert-radio">
          <input type="checkbox" checked={timeOn} onChange={(e) => setTimeOn(e.target.checked)} />
          <span>For time</span>
        </label>
        <div className="mvp-alert-line">
          <span>If project reaches</span>
          <span className="mvp-alert-percent">
            <input
              type="number"
              min="0"
              max="100"
              size={Math.max(String(timePercent).length, 1)}
              value={timePercent}
              onChange={(e) => setTimePercent(e.target.value)}
            />
            <span>%</span>
          </span>
          <span>of estimate</span>
        </div>
        <label className="mvp-alert-line">
          <span>Alert</span>
          <select value={timeRole} onChange={(e) => setTimeRole(e.target.value)}>
            {ROLE_OPTIONS.map((role) => (
              <option key={role.key} value={role.key}>{role.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="mvp-alert-split" />

      <div className="mvp-alert-block">
        <label className="mvp-alert-radio">
          <input type="checkbox" checked={budgetOn} onChange={(e) => setBudgetOn(e.target.checked)} />
          <span>For budget</span>
        </label>
        <div className="mvp-alert-line">
          <span>If project reaches</span>
          <span className="mvp-alert-percent">
            <input
              type="number"
              min="0"
              max="100"
              size={Math.max(String(budgetPercent).length, 1)}
              value={budgetPercent}
              onChange={(e) => setBudgetPercent(e.target.value)}
            />
            <span>%</span>
          </span>
          <span>of estimate</span>
        </div>
        <label className="mvp-alert-line">
          <span>Alert</span>
          <select value={budgetRole} onChange={(e) => setBudgetRole(e.target.value)}>
            {ROLE_OPTIONS.map((role) => (
              <option key={role.key} value={role.key}>{role.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="mvp-alert-split" />

      <div className="sprint-btn-outer mvp-alert-actions">
        <button type="button" className="submit-client cancel-sprint" onClick={() => save(true)} disabled={saving}>
          <img src={Delete} alt="" />
          Delete Alert
        </button>
        <button type="button" className="submit-client save-sprint" onClick={() => save(false)} disabled={saving}>
          Save Changes
        </button>
      </div>
    </Modal>
  );
};

export default MvpProjectAlertsModal;
